import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Users, AlignLeft, ImageIcon, Plus, Loader2, Upload, Shield } from 'lucide-react'
import { mediaService } from '../../services/mediaService'
import { useAuth } from '../../contexts/AuthContext'
import { finalizeEvent } from 'nostr-tools/pure'
import { RELAYS } from '../../config/constants'

interface CreateRoomModalProps {
    isOpen: boolean
    onClose: () => void
}

export function CreateRoomModal({ isOpen, onClose }: CreateRoomModalProps) {
    const { pubkey, sk, loginMethod } = useAuth()
    const [formData, setFormData] = useState({
        name: '',
        about: '',
        picture: '',
        isPrivate: false
    })
    const [isSaving, setIsSaving] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !loginMethod) return

        e.target.value = ''
        if (file.size > 5 * 1024 * 1024) {
            alert('Resim boyutu 5MB\'dan küçük olmalıdır.')
            return
        }

        setIsUploading(true)
        try {
            const url = await mediaService.uploadFile(file, loginMethod, sk || undefined)
            setFormData(prev => ({ ...prev, picture: url }))
        } catch (error) {
            console.error('Upload failed:', error)
            alert('Resim yüklenemedi.')
        } finally {
            setIsUploading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.name.trim() || !pubkey) return

        setIsSaving(true)
        try {
            // NIP-29 Kind 39000 (Group Metadata)
            // Reference: https://github.com/nostr-protocol/nips/blob/master/29.md
            const eventTemplate = {
                kind: 39000,
                created_at: Math.floor(Date.now() / 1000),
                tags: [
                    ['d', formData.name.toLowerCase().replace(/\s+/g, '-')], // identifier
                    ['name', formData.name],
                    ['about', formData.about],
                    ['picture', formData.picture],
                    ['t', 'mavililer'] // Site-local tag
                ],
                content: '',
                pubkey: pubkey
            }

            let signedEvent;
            if (sk) {
                signedEvent = finalizeEvent(eventTemplate, sk)
            } else if (window.nostr) {
                signedEvent = await window.nostr.signEvent(eventTemplate)
            } else {
                throw new Error('No signing method available')
            }

            // Publish to relays
            const publishPromises = RELAYS.map((url: string) => {
                return new Promise((resolve) => {
                    const ws = new WebSocket(url)
                    const timeout = setTimeout(() => { ws.close(); resolve(false); }, 5000)
                    ws.onopen = () => {
                        ws.send(JSON.stringify(['EVENT', signedEvent]))
                    }
                    ws.onmessage = (msg) => {
                        const [type, , status] = JSON.parse(msg.data)
                        if (type === 'OK') {
                            clearTimeout(timeout)
                            ws.close()
                            resolve(status)
                        }
                    }
                    ws.onerror = () => { ws.close(); resolve(false); }
                })
            })

            await Promise.race(publishPromises)

            onClose()
            setFormData({ name: '', about: '', picture: '', isPrivate: false })
            // Trigger a refresh (we could use a global state or event emitter)
            window.location.reload()
        } catch (error) {
            console.error('Failed to create room:', error)
            alert('Oda oluşturulurken bir hata oluştu.')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110]"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
                        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                        exit={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
                        className="fixed top-1/2 left-1/2 w-full max-w-lg bg-white dark:bg-surface-900 rounded-[2.5rem] shadow-2xl z-[120] overflow-hidden border border-surface-200 dark:border-surface-700"
                    >
                        {/* Header */}
                        <div className="bg-mavi-600 p-8 flex items-center justify-between text-white relative">
                            <div className="relative z-10">
                                <h2 className="font-poppins font-bold text-2xl">Oda Oluştur</h2>
                                <p className="text-[10px] text-white/70 font-poppins uppercase tracking-widest mt-1">Yeni bir topluluk başlat</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/10 rounded-xl transition-all"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6">
                            {/* Room Name */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <Users className="w-3 h-3" /> Oda İsmi
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-poppins"
                                    placeholder="Örn: Nostr Geliştiricileri"
                                />
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <AlignLeft className="w-3 h-3" /> Açıklama
                                </label>
                                <textarea
                                    value={formData.about}
                                    onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                                    className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-poppins h-24 resize-none"
                                    placeholder="Bu oda ne hakkında?"
                                />
                            </div>

                            {/* Picture Upload */}
                            <div className="space-y-2">
                                <label className="flex items-center justify-between text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    <span className="flex items-center gap-2"><ImageIcon className="w-3 h-3" /> Oda Resmi</span>
                                    {isUploading && <span className="text-mavi-500 flex items-center gap-1 normal-case"><Loader2 className="w-3 h-3 animate-spin" /> Yükleniyor...</span>}
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <input
                                            type="url"
                                            value={formData.picture}
                                            onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
                                            className="w-full p-4 bg-surface-50 dark:bg-surface-800/50 border border-surface-100 dark:border-surface-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500/50 transition-all font-mono"
                                            placeholder="https://example.com/image.jpg"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                        className="p-4 bg-mavi-500 hover:bg-mavi-600 text-white rounded-2xl transition-all shadow-md hover:shadow-lg disabled:opacity-50"
                                    >
                                        <Upload className="w-5 h-5" />
                                    </button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                </div>
                            </div>

                            {/* Visibility Status */}
                            <div className="flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-800/30 rounded-2xl border border-surface-100 dark:border-surface-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-500">
                                        <Shield className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-poppins font-bold text-gray-800 dark:text-gray-200">Açık Topluluk</h4>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400">Herkes katılabilir</p>
                                    </div>
                                </div>
                                <div className="px-3 py-1 bg-green-500/10 text-green-500 text-[10px] font-poppins font-bold rounded-full border border-green-500/20">
                                    AKTİF
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 py-4 px-6 rounded-2xl bg-surface-100 dark:bg-surface-800 text-gray-700 dark:text-gray-300 font-poppins font-bold text-sm hover:bg-surface-200 dark:hover:bg-surface-700 transition-all"
                                >
                                    Vazgeç
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving || !formData.name}
                                    className="flex-[2] py-4 px-6 rounded-2xl bg-mavi-600 text-white font-poppins font-bold text-sm hover:bg-mavi-700 transition-all shadow-lg shadow-mavi-500/30 flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Oluşturuluyor...
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="w-4 h-4" />
                                            Odayı Başlat
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
